import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSuperAdminAuth } from '../../../contexts/SuperAdminAuthContext';
import { governanceAPI } from '../../../lib/governanceApi';
import { toast } from 'sonner';
import {
  RefreshCw, Pause, Play, Eye, MoreHorizontal, Building2, AlertTriangle,
  Beaker, DollarSign, Phone,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import {
  PageHeader, MetricRow, DataTable, FilterBar, StatusDot, ConfirmDestructive,
} from '../design';

/**
 * Governor → Tenants — Iter 62.7 redesign.
 *
 * Replaces a 675-LOC hand-rolled table with the universal <DataTable />.
 * Adds: row-click drill-in, ⋯ overflow actions, bulk pause/resume, hide-test
 * toggle, URL-state filters via FilterBar chips.
 */

const STATUS_FILTER_OPTIONS = [
  { value: 'enabled', label: 'AI Enabled' },
  { value: 'paused',  label: 'AI Paused' },
];
const DEMO_FILTER_OPTIONS = [
  { value: 'production', label: 'Production' },
  { value: 'demo',       label: 'Demo / Test' },
];
const BUDGET_FILTER_OPTIONS = [
  { value: 'critical', label: 'Critical (≥90%)' },
  { value: 'warning',  label: 'Warning (70-90%)' },
  { value: 'healthy',  label: 'Healthy (<70%)' },
];
const EXEC_FILTER_OPTIONS = [
  { value: 'live',      label: 'LIVE' },
  { value: 'simulated', label: 'SIMULATED' },
];
const BILLING_FILTER_OPTIONS = [
  { value: 'paid',      label: 'Paid' },
  { value: 'trialing',  label: 'Trialing' },
  { value: 'past_due',  label: 'Past-due' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'pending',   label: 'Pending' },
  { value: 'free',      label: 'Free' },
];
const PLAN_FILTER_OPTIONS = [
  { value: 'free',       label: 'Free' },
  { value: 'growth',     label: 'Growth' },
  { value: 'scale',      label: 'Scale' },
  { value: 'enterprise', label: 'Enterprise' },
  { value: 'manual_pro', label: 'Manual Pro' },
];
const BILLING_TONE = {
  paid:      'border-emerald-500/40 bg-emerald-500/10 text-emerald-400',
  trialing:  'border-sky-500/40 bg-sky-500/10 text-sky-400',
  past_due:  'border-rose-500/40 bg-rose-500/10 text-rose-400',
  cancelled: 'border-slate-500/40 bg-slate-500/10 text-slate-400',
  pending:   'border-amber-500/40 bg-amber-500/10 text-amber-400',
  free:      'border-slate-500/40 bg-slate-500/10 text-slate-400',
};
const BILLING_LABEL = {
  paid: 'Paid', trialing: 'Trialing', past_due: 'Past-due',
  cancelled: 'Cancelled', pending: 'Pending', free: 'Free',
};
const PAGE_SIZE = 50;

export default function Tenants() {
  const { isAuthenticated } = useSuperAdminAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [organizations, setOrganizations] = useState([]);

  const [selectedOrg, setSelectedOrg] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [demoFilter, setDemoFilter] = useState('all');
  const [budgetFilter, setBudgetFilter] = useState('all');
  const [executionModeFilter, setExecutionModeFilter] = useState('all');
  const [billingFilter, setBillingFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [hideTest, setHideTest] = useState(true);
  // Server-side pagination state
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  // Past-due churn radar (server-side count, independent of the active filter)
  const [pastDueCount, setPastDueCount] = useState(0);

  // VoiceLink "adopt an existing number" (governor cross-tenant)
  const [adoptTenant, setAdoptTenant] = useState(null);
  const [adoptPhone, setAdoptPhone] = useState('');
  const [adoptDid, setAdoptDid] = useState('');
  const [adoptClient, setAdoptClient] = useState('');
  const [adoptBusy, setAdoptBusy] = useState(false);

  // VoiceLink "force-restore" — number already owned+routed on VoiceLink, bring it live
  const [restoreTenant, setRestoreTenant] = useState(null);
  const [restorePhone, setRestorePhone] = useState('');
  const [restoreClient, setRestoreClient] = useState('');
  const [restoreBusy, setRestoreBusy] = useState(false);

  const submitRestore = async () => {
    if (!restorePhone.trim()) { toast.error('Enter the phone number.'); return; }
    setRestoreBusy(true);
    try {
      const payload = { tenant_id: restoreTenant.tenant_id, phone_number: restorePhone.trim() };
      if (restoreClient.trim()) payload.client_id = parseInt(restoreClient, 10);
      const res = await governanceAPI.forceRestoreVoicelinkForTenant(payload);
      toast.success(`${res.data?.phone_number} is live now for ${restoreTenant.name}.`);
      setRestoreTenant(null); setRestorePhone(''); setRestoreClient('');
      fetchTenants();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not force-restore number');
    } finally { setRestoreBusy(false); }
  };

  const submitAdopt = async () => {
    const did = parseInt(adoptDid, 10);
    if (!adoptPhone.trim() || !did) { toast.error('Enter the phone number and VoiceLink DID id.'); return; }
    setAdoptBusy(true);
    try {
      const payload = { tenant_id: adoptTenant.tenant_id, did_id: did, phone_number: adoptPhone.trim() };
      if (adoptClient.trim()) payload.client_id = parseInt(adoptClient, 10);
      const res = await governanceAPI.adoptVoicelinkForTenant(payload);
      toast.success(`${res.data?.phone_number} adopted for ${adoptTenant.name} — they can now reclaim it.`);
      setAdoptTenant(null); setAdoptPhone(''); setAdoptDid(''); setAdoptClient('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not adopt number');
    } finally { setAdoptBusy(false); }
  };

  const fetchTenants = useCallback(async (over = {}) => {
    const opts = {
      orgId: (over.orgFilter ?? selectedOrg) === 'all' ? null : (over.orgFilter ?? selectedOrg),
      q: over.q ?? searchQuery,
      plan: over.plan ?? planFilter,
      billing: over.billing ?? billingFilter,
      demo: over.demo ?? demoFilter,
      hideTest: over.hideTest ?? hideTest,
      skip: (over.page ?? page) * PAGE_SIZE,
      limit: PAGE_SIZE,
    };
    setLoading(true);
    try {
      const res = await governanceAPI.getTenantsEnhanced(opts);
      const data = res.data || {};
      // New wrapper shape {items,total,has_more}; tolerate a legacy list too.
      if (Array.isArray(data)) {
        setTenants(data); setTotal(data.length); setHasMore(false);
      } else {
        setTenants(data.items || []); setTotal(data.total || 0); setHasMore(!!data.has_more);
      }
    } catch (_e) {
      try {
        const orgId = opts.orgId;
        const res = await governanceAPI.getTenants(orgId);
        setTenants(res.data || []); setTotal((res.data || []).length); setHasMore(false);
      } catch (_e2) {
        toast.error('Failed to load tenants');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, searchQuery, planFilter, billingFilter, demoFilter, hideTest, page]);

  useEffect(() => {
    if (isAuthenticated) {
      governanceAPI.getOrganizations().then((r) => setOrganizations(r.data || [])).catch(() => {});
    }
  }, [isAuthenticated]);

  // Past-due count for the churn-radar badge — a cheap limit=1 call that
  // reads the server-side `total`, so it stays accurate regardless of the
  // filter/page the governor is currently viewing.
  useEffect(() => {
    if (!isAuthenticated) return;
    governanceAPI
      .getTenantsEnhanced({ billing: 'past_due', hideTest, limit: 1 })
      .then((r) => setPastDueCount(r.data?.total || 0))
      .catch(() => {});
  }, [isAuthenticated, hideTest, tenants]);

  // Debounced server-side search/filter/pagination refetch.
  useEffect(() => {
    if (!isAuthenticated) return;
    const t = setTimeout(() => { fetchTenants(); }, 300);
    return () => clearTimeout(t);
  }, [isAuthenticated, fetchTenants]);

  // Reset to page 0 whenever a server-side filter/search changes.
  useEffect(() => { setPage(0); }, [selectedOrg, searchQuery, planFilter, billingFilter, demoFilter, hideTest]);

  const filteredTenants = useMemo(() => {
    return tenants.filter((t) => {
      // Search + plan + billing + org + demo/test exclusion are applied
      // server-side; the remaining filters refine the current page.
      if (statusFilter === 'enabled' && t.kill_switch_active) return false;
      if (statusFilter === 'paused'  && !t.kill_switch_active) return false;
      const usage = t.budget_usage_percent || 0;
      if (budgetFilter === 'critical' && usage < 90) return false;
      if (budgetFilter === 'warning'  && (usage < 70 || usage >= 90)) return false;
      if (budgetFilter === 'healthy'  && usage >= 70) return false;
      const mode = (t.execution_mode || 'SIMULATED').toUpperCase();
      if (executionModeFilter === 'live' && mode !== 'LIVE') return false;
      if (executionModeFilter === 'simulated' && mode !== 'SIMULATED') return false;
      return true;
    });
  }, [tenants, statusFilter, budgetFilter, executionModeFilter]);

  const resetFilters = () => {
    setSelectedOrg('all'); setSearchQuery(''); setStatusFilter('all');
    setDemoFilter('all'); setBudgetFilter('all'); setExecutionModeFilter('all');
    setBillingFilter('all'); setPlanFilter('all'); setPage(0);
  };

  const executeAction = async (tenant, action, reason) => {
    try {
      await governanceAPI.executeOrgAction(tenant.org_id, {
        action,
        tenant_id: tenant.tenant_id,
        config: { reason: reason || 'Governor action' },
      });
      toast.success(`${action.toLowerCase()} → ${tenant.name}`);
      fetchTenants();
    } catch (_e) {
      toast.error('Action failed');
    }
  };

  // Stats
  const totalT     = filteredTenants.length;
  const activeT    = filteredTenants.filter(t => !t.kill_switch_active).length;
  const pausedT    = filteredTenants.filter(t =>  t.kill_switch_active).length;
  const budgetRisk = filteredTenants.filter(t => (t.budget_usage_percent || 0) >= 80).length;
  const totalSpend = filteredTenants.reduce((s, t) => s + (t.cost_mtd || 0), 0);

  // ---- DataTable columns ---------------------------------------------------
  const columns = useMemo(() => [
    {
      accessorKey: 'name',
      header: 'Tenant',
      cell: ({ row }) => {
        const t = row.original;
        return (
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[var(--gov-accent-bg)] ring-1 ring-[var(--gov-accent-border)]">
              <Building2 className="h-3.5 w-3.5 text-[var(--gov-accent)]" />
            </div>
            <div className="min-w-0">
              <div className="gov-text-primary truncate text-sm font-medium">{t.name}</div>
              <div className="gov-text-tertiary gov-mono truncate text-[11px]">{t.tenant_id?.slice(0, 12)}…</div>
            </div>
            {t.is_demo && (
              <Badge variant="outline" className="ml-1 border-amber-500/40 bg-amber-500/10 text-[10px] text-amber-400">
                <Beaker className="mr-0.5 h-2.5 w-2.5" /> demo
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'kill_switch_active',
      header: 'AI Status',
      size: 130,
      cell: ({ row }) => (
        <StatusDot
          status={row.original.kill_switch_active ? 'paused' : 'enabled'}
          variant="pill"
        />
      ),
    },
    {
      accessorKey: 'execution_mode',
      header: 'Mode',
      size: 100,
      cell: ({ row }) => {
        const isLive = (row.original.execution_mode || '').toUpperCase() === 'LIVE';
        return (
          <Badge variant="outline" className={isLive
            ? 'border-emerald-500/40 bg-emerald-500/10 text-[11px] text-emerald-400'
            : 'border-slate-500/40 bg-slate-500/10 text-[11px] text-slate-400'}>
            {isLive ? 'LIVE' : 'SIM'}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'plan_id',
      header: 'Plan',
      size: 110,
      cell: ({ row }) => {
        const p = row.original.plan_id || 'free';
        const label = (PLAN_FILTER_OPTIONS.find((o) => o.value === p) || {}).label
          || (p.charAt(0).toUpperCase() + p.slice(1));
        return (
          <Badge variant="outline" className="border-[var(--gov-border)] bg-[var(--gov-bg-input)] text-[11px] gov-text-secondary" data-testid={`tenant-plan-${row.original.tenant_id}`}>
            {label}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'billing_status',
      header: 'Billing',
      size: 110,
      cell: ({ row }) => {
        const b = row.original.billing_status || 'free';
        const tone = BILLING_TONE[b] || BILLING_TONE.free;
        return (
          <Badge variant="outline" className={`text-[11px] ${tone}`} data-testid={`tenant-billing-${row.original.tenant_id}`}>
            {BILLING_LABEL[b] || b}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'budget_usage_percent',
      header: 'Budget',
      size: 140,
      cell: ({ row }) => {
        const pct = row.original.budget_usage_percent || 0;
        const tone = pct >= 90 ? 'rose' : pct >= 70 ? 'amber' : 'emerald';
        const bg = { rose: 'bg-rose-500', amber: 'bg-amber-500', emerald: 'bg-emerald-500' }[tone];
        return (
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-16 overflow-hidden rounded-full bg-[var(--gov-bg-elevated)]">
              <div className={`h-full ${bg} transition-all`} style={{ width: `${Math.min(pct, 100)}%` }} />
            </div>
            <span className="gov-text-secondary gov-tabular text-xs">{pct}%</span>
          </div>
        );
      },
    },
    {
      accessorKey: 'cost_mtd',
      header: 'MTD Spend',
      size: 110,
      cell: ({ row }) => (
        <span className="gov-tabular gov-text-primary text-sm">${(row.original.cost_mtd || 0).toFixed(2)}</span>
      ),
    },
    {
      accessorKey: 'users_count',
      header: 'Users',
      size: 80,
      cell: ({ row }) => (
        <span className="gov-tabular gov-text-secondary text-sm">{row.original.users_count || 0}</span>
      ),
    },
    {
      id: '_actions',
      header: '',
      enableSorting: false,
      size: 48,
      cell: ({ row }) => {
        const t = row.original;
        return (
          <div onClick={(e) => e.stopPropagation()} className="text-right">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`tenant-actions-${t.tenant_id}`}>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem onClick={() => navigate(`/governance/tenants/${t.tenant_id}`)}>
                  <Eye className="mr-2 h-3.5 w-3.5" /> View details
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { setAdoptPhone(''); setAdoptDid(''); setAdoptClient(''); setAdoptTenant(t); }} data-testid={`adopt-voicelink-${t.tenant_id}`}>
                  <Phone className="mr-2 h-3.5 w-3.5 text-cyan-500" /> Adopt VoiceLink #
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { setRestorePhone(''); setRestoreClient(''); setRestoreTenant(t); }} data-testid={`force-restore-voicelink-${t.tenant_id}`}>
                  <RefreshCw className="mr-2 h-3.5 w-3.5 text-emerald-500" /> Force-restore #
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {t.kill_switch_active ? (
                  <DropdownMenuItem onClick={() => executeAction(t, 'TENANT_KILL_SWITCH_OFF', 'Re-enabled from tenants list')}>
                    <Play className="mr-2 h-3.5 w-3.5 text-emerald-500" /> Enable AI
                  </DropdownMenuItem>
                ) : (
                  <ConfirmDestructive
                    trigger={
                      <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                        <Pause className="mr-2 h-3.5 w-3.5 text-rose-500" /> Pause AI
                      </DropdownMenuItem>
                    }
                    title={`Pause AI for ${t.name}?`}
                    description="All AI workers (cadence emails, calls, scoring) will stop for this tenant immediately. Customer-visible."
                    confirmLabel="Pause AI"
                    onConfirm={(reason) => executeAction(t, 'TENANT_KILL_SWITCH_ON', reason)}
                    testId={`pause-tenant-${t.tenant_id}`}
                  />
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ], [navigate]);

  const bulkPause = async (rows, clearSelection) => {
    await Promise.all(rows.map((t) => executeAction(t, 'TENANT_KILL_SWITCH_ON', 'Bulk pause')));
    clearSelection();
  };
  const bulkResume = async (rows, clearSelection) => {
    await Promise.all(rows.map((t) => executeAction(t, 'TENANT_KILL_SWITCH_OFF', 'Bulk resume')));
    clearSelection();
  };

  return (
    <div className="space-y-6" data-testid="tenants-page">
      <PageHeader
        eyebrow="Governance"
        title="Tenants"
        subtitle={`${total} total${filteredTenants.length !== tenants.length ? ` · ${filteredTenants.length} shown after page filters` : ''} · page ${page + 1} · click any row to drill in`}
        actions={
          <>
            {pastDueCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setBillingFilter((v) => (v === 'past_due' ? 'all' : 'past_due'))}
                className={`h-9 gap-1.5 border-rose-500/40 bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 ${billingFilter === 'past_due' ? 'ring-1 ring-rose-500/60' : ''}`}
                data-testid="past-due-quick-filter"
                title="Show tenants whose payment failed — catch churn fast"
              >
                <AlertTriangle className="h-3.5 w-3.5" />
                Past-due
                <span
                  className="ml-0.5 rounded-full bg-rose-500/30 px-1.5 py-0.5 text-[10px] font-semibold text-rose-200"
                  data-testid="past-due-count-badge"
                >
                  {pastDueCount}
                </span>
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setHideTest((v) => !v)}
              className="h-9 border-[var(--gov-border)] bg-[var(--gov-bg-input)]"
              data-testid="toggle-hide-test"
            >
              {hideTest ? 'Show test/demo' : 'Hide test/demo'}
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => fetchTenants()}
              className="h-9 w-9 border-[var(--gov-border)] bg-[var(--gov-bg-input)]"
              data-testid="refresh-tenants"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </>
        }
      />

      <MetricRow
        cols={5}
        metrics={[
          { key: 'total',  label: 'Showing',      value: totalT,    icon: Building2 },
          { key: 'active', label: 'AI Enabled',   value: activeT,   icon: Play, tone: 'success' },
          { key: 'paused', label: 'AI Paused',    value: pausedT,   icon: Pause, tone: pausedT > 0 ? 'danger' : 'muted' },
          { key: 'risk',   label: 'Budget Risk',  value: budgetRisk, icon: AlertTriangle, tone: budgetRisk > 0 ? 'warning' : 'muted' },
          { key: 'spend',  label: 'Total MTD',    value: `$${totalSpend.toFixed(2)}`, icon: DollarSign },
        ]}
      />

      <FilterBar
        search={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search by tenant name, ID or organization…',
        }}
        filters={[
          { key: 'org', label: 'Organization', value: selectedOrg, onChange: setSelectedOrg,
            defaultLabel: 'All organizations',
            options: organizations.map((o) => ({ value: o.org_id, label: o.name })) },
          { key: 'billing', label: 'Billing',  value: billingFilter, onChange: setBillingFilter, options: BILLING_FILTER_OPTIONS },
          { key: 'plan',   label: 'Plan',       value: planFilter,   onChange: setPlanFilter,   options: PLAN_FILTER_OPTIONS },
          { key: 'status', label: 'AI Status', value: statusFilter, onChange: setStatusFilter, options: STATUS_FILTER_OPTIONS },
          { key: 'demo',   label: 'Type',      value: demoFilter,   onChange: setDemoFilter,   options: DEMO_FILTER_OPTIONS },
          { key: 'budget', label: 'Budget',    value: budgetFilter, onChange: setBudgetFilter, options: BUDGET_FILTER_OPTIONS },
          { key: 'mode',   label: 'Mode',      value: executionModeFilter, onChange: setExecutionModeFilter, options: EXEC_FILTER_OPTIONS },
        ]}
        resultCount={total}
        onReset={resetFilters}
      />

      <DataTable
        columns={columns}
        data={filteredTenants}
        loading={loading}
        onRowClick={(t) => navigate(`/governance/tenants/${t.tenant_id}`)}
        bulkActions={(rows, clearSelection) => (
          <>
            <ConfirmDestructive
              trigger={
                <Button variant="destructive" size="sm" className="h-7 text-xs">
                  <Pause className="mr-1 h-3 w-3" /> Pause {rows.length}
                </Button>
              }
              title={`Pause AI for ${rows.length} tenants?`}
              description="This stops every AI worker across all selected tenants. Customer-visible. Recorded in audit log."
              confirmLabel={`Pause ${rows.length} tenants`}
              onConfirm={() => bulkPause(rows, clearSelection)}
              testId="bulk-pause-tenants"
            />
            <Button
              variant="outline" size="sm" className="h-7 text-xs"
              onClick={() => bulkResume(rows, clearSelection)}
              data-testid="bulk-resume-tenants"
            >
              <Play className="mr-1 h-3 w-3" /> Resume {rows.length}
            </Button>
          </>
        )}
        emptyTitle="No tenants match your filters"
        emptyHint={hideTest ? 'Try toggling "Show test/demo" or clearing filters.' : 'Try clearing filters.'}
        emptyIcon={Building2}
        testId="tenants-table"
      />

      {/* Server-side pagination */}
      <div className="flex items-center justify-between" data-testid="tenants-pagination">
        <span className="gov-text-tertiary text-xs">
          {total === 0 ? 'No results' : `Showing ${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, total)} of ${total}`}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            className="h-8 border-[var(--gov-border)] bg-[var(--gov-bg-input)] text-xs"
            disabled={page === 0 || loading}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            data-testid="tenants-prev-page"
          >
            Previous
          </Button>
          <span className="gov-text-secondary text-xs">Page {page + 1}</span>
          <Button
            variant="outline" size="sm"
            className="h-8 border-[var(--gov-border)] bg-[var(--gov-bg-input)] text-xs"
            disabled={!hasMore || loading}
            onClick={() => setPage((p) => p + 1)}
            data-testid="tenants-next-page"
          >
            Next
          </Button>
        </div>
      </div>

      {/* Governor: adopt an existing VoiceLink DID for a tenant */}
      <Dialog open={!!adoptTenant} onOpenChange={(o) => { if (!o) setAdoptTenant(null); }}>
        <DialogContent data-testid="gov-adopt-dialog">
          <DialogHeader>
            <DialogTitle>Adopt a VoiceLink number</DialogTitle>
            <DialogDescription>
              Attach a DID that <b>{adoptTenant?.name}</b> already owns on the VoiceLink panel, so their “Reclaim my number” banner appears. This only links the number in the CRM — reconnecting it still needs VoiceLink reachable.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <label className="text-xs text-muted-foreground">Phone number (E.164)</label>
              <Input value={adoptPhone} onChange={(e) => setAdoptPhone(e.target.value)} placeholder="+919228130611" data-testid="gov-adopt-phone" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">VoiceLink DID id</label>
              <Input value={adoptDid} onChange={(e) => setAdoptDid(e.target.value)} placeholder="e.g. 3003" data-testid="gov-adopt-did" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">VoiceLink client id (optional — only if not already linked)</label>
              <Input value={adoptClient} onChange={(e) => setAdoptClient(e.target.value)} placeholder="e.g. 1919" data-testid="gov-adopt-client" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdoptTenant(null)} data-testid="gov-adopt-cancel">Cancel</Button>
            <Button onClick={submitAdopt} disabled={adoptBusy} data-testid="gov-adopt-submit">
              {adoptBusy ? <RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Phone className="mr-1.5 h-3.5 w-3.5" />}Adopt number
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Governor: force-restore a VoiceLink number already owned + routed on VoiceLink */}
      <Dialog open={!!restoreTenant} onOpenChange={(o) => { if (!o) setRestoreTenant(null); }}>
        <DialogContent data-testid="gov-restore-dialog">
          <DialogHeader>
            <DialogTitle>Force-restore a VoiceLink number</DialogTitle>
            <DialogDescription>
              Bring <b>{restoreTenant?.name}</b> LIVE for a number that is <b>already assigned &amp; routed</b> on the VoiceLink panel. Skips the purchase/map steps (no DID id needed), re-registers the ElevenLabs phone, and marks it ready. No charge — the number is already paid.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <label className="text-xs text-muted-foreground">Phone number (E.164)</label>
              <Input value={restorePhone} onChange={(e) => setRestorePhone(e.target.value)} placeholder="+919228130611" data-testid="gov-restore-phone" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">VoiceLink client id (optional — only if not already linked)</label>
              <Input value={restoreClient} onChange={(e) => setRestoreClient(e.target.value)} placeholder="e.g. 2300" data-testid="gov-restore-client" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreTenant(null)} data-testid="gov-restore-cancel">Cancel</Button>
            <Button onClick={submitRestore} disabled={restoreBusy} data-testid="gov-restore-submit">
              {restoreBusy ? <RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1.5 h-3.5 w-3.5" />}Force-restore
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
